import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Cloud, Download, Star, Trash2, Upload } from "lucide-react";
import { Card, EmptyState, TypeBadge } from "@/components/common";
import { shortHash, timeAgo } from "@/lib/format";
import { addToWatchlist, exportWatchlist, getWatchlist, importWatchlist, removeFromWatchlist } from "@/lib/watchlist";
import { getSession, pullAndMergeWatchlist } from "@/lib/wallet";
import { usePageTitle } from "@/lib/seo";

const CATS = [
  { id: "all", label: "All" },
  { id: "wallet", label: "Wallets" },
  { id: "token", label: "Tokens" },
  { id: "stock", label: "Stock Tokens" },
  { id: "contract", label: "Contracts" },
];

export default function WatchlistPage() {
  const [list, setList] = useState(getWatchlist());
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [type, setType] = useState("wallet");
  const [session, setSession] = useState(getSession());
  const fileRef = useRef(null);
  usePageTitle("Watchlist", "Track your favorite wallets, tokens, stock tokens and contracts on Robinhood Chain.");

  useEffect(() => {
    const onWallet = () => {
      setSession(getSession());
      setList(getWatchlist());
    };
    window.addEventListener("gmscan:wallet-changed", onWallet);
    if (getSession()) pullAndMergeWatchlist().then((l) => l && setList(l)).catch(() => {});
    return () => window.removeEventListener("gmscan:wallet-changed", onWallet);
  }, []);

  const filtered = list.filter((i) =>
    (cat === "all" || i.type === cat) &&
    (!q || i.value.toLowerCase().includes(q.toLowerCase()) || (i.label || "").toLowerCase().includes(q.toLowerCase()))
  );

  const add = (e) => {
    e.preventDefault();
    if (!/^0x[a-fA-F0-9]{40}$/.test(value.trim())) {
      toast.error("Please enter a valid 0x address");
      return;
    }
    setList(addToWatchlist({ type, value: value.trim(), label: label.trim() || shortHash(value.trim()) }));
    setValue(""); setLabel("");
    toast.success("Added to watchlist");
  };

  const linkFor = (i) => (i.type === "token" || i.type === "stock" ? `/token/${i.value}` : `/address/${i.value}`);

  return (
    <div className="space-y-6 pt-8">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Star size={20} className="text-primary" /> Watchlist
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Track wallets, tokens, stock tokens and contracts. No account needed.</p>
      </div>

      {session && (
        <Card className="p-3 flex items-center gap-2 border-primary/30 bg-primary/5" data-testid="watchlist-sync-status">
          <Cloud size={14} className="text-primary shrink-0" />
          <p className="text-xs text-muted-foreground">
            Synced with wallet <span className="font-mono text-primary">{shortHash(session.address, 4)}</span> — changes are
            saved to your wallet account automatically and follow you across devices.
          </p>
        </Card>
      )}

      <Card className="p-4">
        <form onSubmit={add} className="flex flex-wrap gap-2">
          <select data-testid="watchlist-type-select" value={type} onChange={(e) => setType(e.target.value)} className="bg-secondary border rounded-lg text-xs px-2 py-2">
            <option value="wallet">Wallet</option>
            <option value="token">Token</option>
            <option value="stock">Stock Token</option>
            <option value="contract">Contract</option>
          </select>
          <input data-testid="watchlist-address-input" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0x address…"
            className="flex-1 min-w-[200px] bg-card border rounded-lg px-3 py-2 text-xs font-mono outline-none focus:border-primary" />
          <input data-testid="watchlist-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Custom name (optional)"
            className="w-44 bg-card border rounded-lg px-3 py-2 text-xs outline-none focus:border-primary" />
          <button data-testid="watchlist-add-btn" className="text-xs rounded-lg px-4 py-2 bg-primary text-primary-foreground font-semibold">Add</button>
        </form>
      </Card>

      <div className="flex flex-wrap gap-2 items-center">
        {CATS.map((c) => (
          <button key={c.id} data-testid={`watchlist-cat-${c.id}`} onClick={() => setCat(c.id)}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors duration-150 ${cat === c.id ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground"}`}>
            {c.label}
          </button>
        ))}
        <input data-testid="watchlist-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…"
          className="w-40 bg-card border rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary" />
        <div className="ml-auto flex gap-2">
          <button data-testid="watchlist-export-btn" onClick={() => { exportWatchlist(); toast.success("Watchlist exported"); }}
            className="text-xs border rounded-lg px-3 py-1.5 flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors duration-150">
            <Download size={12} /> Export
          </button>
          <button data-testid="watchlist-import-btn" onClick={() => fileRef.current?.click()}
            className="text-xs border rounded-lg px-3 py-1.5 flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors duration-150">
            <Upload size={12} /> Import
          </button>
          <input ref={fileRef} type="file" accept=".json" className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) importWatchlist(f).then((l) => { setList(l); toast.success("Watchlist imported"); }).catch(() => toast.error("Invalid watchlist file"));
              e.target.value = "";
            }} />
        </div>
      </div>

      <Card>
        {filtered.length === 0 ? (
          <EmptyState icon={Star} title="Your watchlist is empty" subtitle="Add an address above, or press the Watch button on any wallet, token or contract page." />
        ) : (
          <div className="divide-y">
            {filtered.map((i) => (
              <div key={i.value} className="flex items-center gap-3 px-4 py-3" data-testid="watchlist-item">
                <TypeBadge>{i.type}</TypeBadge>
                <div className="min-w-0">
                  <Link to={linkFor(i)} className="text-sm text-primary hover:underline">{i.label || shortHash(i.value)}</Link>
                  <p className="text-[10px] font-mono text-muted-foreground truncate">{i.value}</p>
                </div>
                <span className="ml-auto text-[10px] text-muted-foreground">added {timeAgo(i.addedAt)}</span>
                <button data-testid="watchlist-remove-btn" onClick={() => { setList(removeFromWatchlist(i.value)); toast("Removed"); }}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-150"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <p className="text-[10px] text-muted-foreground max-w-2xl">
        {session
          ? "✓ This watchlist is synced to your connected wallet account on GMScan's servers, in addition to this browser."
          : "⚠ Your watchlist is stored in this browser only. It may be lost if you clear browser storage or switch devices — use Export to back it up, or connect your wallet (top right) to sync it across devices."}
      </p>
    </div>
  );
}
